// src/components/listing-leads/main-listing-leads.jsx
// Listing (seller) pipeline — Board | List toggle page shared with the Buyer
// pipeline via PipelineViewSwitch (board of stage columns, or a sortable
// list). Backed by the listing_leads collection and seller stages.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import PageHeader from "../PageHeader";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  listenListingLeads,
  updateListingLeadStage,
  updateListingLead,
  classifyAppointmentForLead,
  deleteListingLead,
  updateDealById,
  saveListingLead,
  saveBuyerLead,
  getContactsByIds,
  getDealById,
  getDeletedListingLeads,
  restoreListingLead,
  permanentlyDeleteListingLead,
} from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import { clientNames } from "../../utils/leadClients";
import { LISTING_STAGES, STAGE_COLORS, GRADUATION_STAGE, PARKING_STAGES, getListingStageColor } from "../../utils/listingLeadStages";
import { leadSourceLabel } from "../../utils/leadSourceLabel";
import { excludeTestAgents, isTestAgent } from "../../utils/testAgents";
import {
  isOnLead,
  migrationLeadOwner,
  sweepStuckDeals,
  suspectCandidateLeads,
} from "../../utils/leadOwnership";
import { buildListingRequestPrefill } from "../../utils/listingRequestPrefill";
import { selectVisibleDeletedLeads } from "../../utils/deletedLeads";
import NewListingLeadModal from "./NewListingLeadModal";
import ListingLeadDrawer from "./ListingLeadDrawer";
import PipelineViewSwitch from "../pipeline/PipelineViewSwitch";
import { isoDaysFromNow } from "../../utils/pipelineView";
import { usePersistentAgentFilter } from "../../hooks/usePersistentAgentFilter";
import { useActionDialog } from "../ActionDialog";

// Coerce a possibly-object field (deal.leadsource / agentName are sometimes
// objects) into a plain display string for the lead card.
const flatName = (v) =>
  v && typeof v === "object" && !Array.isArray(v)
    ? v.name || [v.firstname, v.lastname].filter(Boolean).join(" ") || ""
    : v || "";

// Deals Board stages that are really pipeline work — moved into the pipeline
// and retired from the board. Sellers use the mapped stage; buyers fall back
// to New Lead (their pipeline has no listing-appointment stage).
const STAGE_FROM_BOARD = {
  Prospect: "New Lead",
  "Listing Appointment": "Listing Appointment",
};

export default function MainListingLeads() {
  const [{ user, users, listingLeads, employees, deals, leadsource }, dispatch] = useStateValue();
  const { confirm, prompt, dialog } = useActionDialog();
  const [showLost, setShowLost] = useState(false);
  const [showWon, setShowWon] = useState(false);
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // "Viewing: <agent>" filter — shared hook: defaults to My Leads, persists per
  // user. Key (listingViewAgent:<uid>) is unchanged so prior picks carry over.
  const [viewAgent, setViewAgent] = usePersistentAgentFilter(user?.id, "listing");

  useEffect(() => {
    const subscribe = () =>
      listenListingLeads((rows) => {
        dispatch({ type: actionType.STORE_LISTING_LEADS, payload: rows });
      });
    let unsub = subscribe();
    // Re-sync whenever the tab regains focus, so a change made elsewhere (e.g.
    // being removed as a co-agent) takes effect immediately when the agent
    // returns to the board — no manual page refresh needed.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        unsub && unsub();
        unsub = subscribe();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      unsub && unsub();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [dispatch]);

  const leads = useMemo(() => listingLeads || [], [listingLeads]);

  // Deep link: /dashboard/listings?lead=<id> opens that lead's drawer once
  // the list is in. Runs on the FULL list so lost/converted leads open too.
  const location = useLocation();
  // Remember WHICH lead was opened, not merely that one was. A plain boolean
  // made this one-shot for the life of the page: the header search sends you
  // here repeatedly with a different ?lead=, React Router updates the location
  // without remounting, and every result after the first was a dead click.
  const [deepLinkedId, setDeepLinkedId] = useState(null);
  useEffect(() => {
    const target = new URLSearchParams(location.search).get("lead");
    if (!target || target === deepLinkedId || !leads.length) return;
    if (leads.find((l) => l.id === target)) {
      setSelectedId(target);
      setDeepLinkedId(target);
    }
  }, [location.search, leads, deepLinkedId]);

  // ONE-TIME MOVE — pull pre-signing Prospect deals off the Deals Board into
  // the pipelines as New Lead cards (sellers → Listing Pipeline, buyers →
  // Buyers Pipeline). The original deal is soft-deleted (full data preserved)
  // and tagged with the new lead's id; "Sign the Listing" / "Offer Accepted"
  // restores that original deal intact, so this round-trips losslessly and is
  // reversible. Admin-only, guarded (won't re-run on a tagged deal).
  const movedRef = useRef(false);
  useEffect(() => {
    if (movedRef.current) return;
    if (user?.role !== "Admin") return;
    // Wait for the user roster — owner NAMES resolve from it, and the sweep
    // is one-shot: a run before users load would stamp blank names for good.
    // (Deliberately does NOT trip movedRef, so the sweep runs once users land.)
    if (!(users || []).length) return;
    const toMove = (deals || []).filter(
      (d) =>
        STAGE_FROM_BOARD[d?.dealStage] &&
        !(Number(d?.deletedAt) > 0) &&
        !d?.migratedToLeadId &&
        // Request intake deals already have a pipeline lead (created at submit),
        // so don't move them again — that would duplicate the lead. The
        // retired-column cleanup below soft-hides them instead.
        d?.requestType !== "fileRequest" &&
        // No resolvable agent id → DON'T move it. Guessing an owner put other
        // agents' files on the sweeping admin's own board (2026-07-29). The
        // board renders no column for these stages, so skipped deals surface
        // in the "needs an agent" banner below instead — never invisibly.
        migrationLeadOwner(d) !== "",
    );
    if (!toMove.length) return;
    movedRef.current = true;
    (async () => {
      let moved = 0;
      for (const d of toMove) {
        try {
          // Map ALL the deal's clients: first becomes the lead's primary,
          // the rest become co-clients (so nobody is lost moving board→pipeline).
          let contactName = "";
          let coClients = [];
          const ids = (d.customers || []).filter(Boolean);
          if (ids.length) {
            const raw = await getContactsByIds(ids);
            const names = new Map(
              (raw || [])
                .map((c) => normalizeContact(c))
                .filter(Boolean)
                .map((c) => [c.id, c.name || ""]),
            );
            contactName = names.get(ids[0]) || "";
            coClients = ids.slice(1).map((cid) => ({
              contactId: cid,
              contactName: names.get(cid) || "",
            }));
          }
          const isBuyer = d.dealtype === "Buyer";
          // Buyers have no listing-appointment stage — fall back to New Lead.
          const stage = isBuyer ? "New Lead" : STAGE_FROM_BOARD[d.dealStage];
          const owner = migrationLeadOwner(d);
          const common = {
            contactName: contactName || d.clientLastName || "",
            contactId: ids[0] || "",
            coClients,
            source: flatName(d.leadsource),
            stage,
            notes: d.notes || "",
            // The lead belongs to the deal's AGENT (else the deal's creator) —
            // never the admin running the move. Deals that resolve to neither
            // are filtered out above and stay on the board.
            assignedAgent: owner,
            // Prefer the deal's own stored name; else resolve from the full
            // user roster (employees misses Supervisors and departed staff).
            assignedAgentName:
              (owner === String(d.agentData || "").trim() &&
                flatName(d.agentName)) ||
              flatName((users || []).find((u) => u.id === owner) || ""),
            createdby: owner,
            sourceDealId: String(d.createdat || ""),
          };
          const res = isBuyer
            ? await saveBuyerLead(common)
            : await saveListingLead({
                  ...common,
                  propertyAddress: d.propertyaddress || "",
                  estValue: d.suggestedPrice || d.offerPrice || "",
                  listPrice: d.listPrice || "",
                });
          if (res?.success) {
            await updateDealById(String(d.createdat), {
              deletedAt: Date.now(),
              migratedToLeadId: res.id,
            });
            moved++;
          }
        } catch (e) {
          console.error("Pipeline move failed for", d?.createdat, e);
        }
      }
      if (moved) {
        toast.success(`Moved ${moved} into the pipeline`);
      }
    })();
  }, [deals, user?.id, users]);

  // One-time repair: an earlier move stamped createdby = the admin who ran it
  // instead of the deal's agent, so a moved lead assigned to another agent
  // (e.g. Joe's "Heather Johnson") wrongly attributed to the admin. Realign
  // createdby to the assigned agent. Only touches MOVED leads (sourceDealId);
  // normally-created leads keep their real creator. Admin-only, idempotent.
  const ownerFixRef = useRef(false);
  useEffect(() => {
    if (ownerFixRef.current) return;
    if (user?.role !== "Admin") return;
    const dirty = (leads || []).filter(
      (l) => l.sourceDealId && l.assignedAgent && l.createdby !== l.assignedAgent,
    );
    if (!dirty.length) return;
    ownerFixRef.current = true;
    dirty.forEach((l) => {
      updateListingLead(l.id, { createdby: l.assignedAgent }).catch((e) =>
        console.error("Lead owner repair failed for", l.id, e),
      );
    });
  }, [leads, user?.id]);

  // One-time name backfill — older listing leads (especially those created from
  // a Listing/Buyer Request) saved only the client's last name, or nothing, in
  // contactName, so the card reads "(unnamed lead)". The link to the real
  // contact (contactId) survived, so resolve the full name from there and patch
  // any lead whose name is blank. Idempotent; admin/supervisor only.
  const nameFixRef = useRef(false);
  useEffect(() => {
    if (nameFixRef.current) return;
    if (user?.role !== "Admin" && user?.role !== "Supervisor") return;
    const missing = (leads || []).filter(
      (l) => l && l.contactId && !String(l.contactName || "").trim(),
    );
    if (!missing.length) return;
    nameFixRef.current = true;
    (async () => {
      try {
        const ids = [...new Set(missing.map((l) => l.contactId))];
        const rows = await getContactsByIds(ids);
        const nameById = {};
        (rows || []).filter(Boolean).forEach((raw) => {
          const name =
            normalizeContact(raw)?.name ||
            raw.name ||
            [raw.firstname, raw.lastname].filter(Boolean).join(" ") ||
            "";
          if (raw.id) nameById[raw.id] = String(name).trim();
        });
        for (const l of missing) {
          const name = nameById[l.contactId];
          if (name) {
            await updateListingLead(
              l.id,
              { contactName: name },
              { skipFubPush: true },
            );
          }
        }
      } catch (e) {
        console.error("Listing-lead name backfill failed:", e);
      }
    })();
  }, [leads, user?.id]);

  // One-time cleanup — soft-hide leftover Humza "Templates"-stage junk deals
  // (not real deals; task templates live in a separate `tasktemplate`
  // collection). Reversible (soft delete), admin-only, idempotent.
  const junkRef = useRef(false);
  useEffect(() => {
    if (junkRef.current) return;
    if (user?.role !== "Admin") return;
    const junk = (deals || []).filter(
      (d) =>
        d?.dealStage === "Templates" &&
        !(Number(d?.deletedAt) > 0) &&
        (isTestAgent({ id: d.agentData }) ||
          isTestAgent({ id: d.createdby }) ||
          isTestAgent({ name: d.agentName })),
    );
    if (!junk.length) return;
    junkRef.current = true;
    junk.forEach((d) => {
      updateDealById(String(d.createdat), { deletedAt: Date.now() }).catch((e) =>
        console.error("Junk-template cleanup failed for", d?.createdat, e),
      );
    });
  }, [deals, user?.id]);

  // One-time cleanup — the Prospect / Listing Appointment board columns are
  // retired (pre-signing work lives in the pipeline now). Soft-hide the leftover
  // Listing/Buyer Request intake deals still parked there (requestType
  // "fileRequest"): each already has a pipeline lead and round-trips to
  // Pre-Market on signing. Anything else in those stages is LEFT UNTOUCHED and
  // flagged in the console for manual re-staging. Reversible, admin-only.
  const retiredColRef = useRef(false);
  useEffect(() => {
    if (retiredColRef.current) return;
    if (user?.role !== "Admin") return;
    const stranded = (deals || []).filter(
      (d) =>
        ["Prospect", "Listing Appointment"].includes(d?.dealStage) &&
        !(Number(d?.deletedAt) > 0),
    );
    if (!stranded.length) return;
    retiredColRef.current = true;
    const requestDeals = stranded.filter((d) => d?.requestType === "fileRequest");
    const others = stranded.filter((d) => d?.requestType !== "fileRequest");
    if (others.length) {
      console.warn(
        `[retired-column cleanup] ${others.length} non-request deal(s) still in Prospect/Listing Appointment — left in place; re-stage manually:`,
        others.map((d) => ({
          id: d.createdat,
          stage: d.dealStage,
          client: d.clientLastName,
          agent: flatName(d.agentName),
        })),
      );
    }
    requestDeals.forEach((d) => {
      updateDealById(String(d.createdat), { deletedAt: Date.now() }).catch((e) =>
        console.error("Retired-column cleanup failed for", d?.createdat, e),
      );
    });
  }, [deals, user?.id]);

  // One-time migration — the "CMA" and "Appt Held" stages were removed
  // (2026-07-15). A lead stranded in a deleted stage silently drops out of
  // every funnel column and stage row, so re-stage them: CMA → Listing
  // Appointment, Appt Held → Appt Set (Henry's mapping). Reversible, admin-only,
  // idempotent (after the move no lead matches). skipFubPush avoids sync churn.
  const RETIRED_STAGE_MAP = { CMA: "Listing Appointment", "Appt Held": "Appt Set" };
  const stageMigrateRef = useRef(false);
  useEffect(() => {
    if (stageMigrateRef.current) return;
    if (user?.role !== "Admin") return;
    const stranded = (leads || []).filter((l) => RETIRED_STAGE_MAP[l?.stage]);
    if (!stranded.length) return;
    stageMigrateRef.current = true;
    stranded.forEach((l) => {
      updateListingLead(
        l.id,
        { stage: RETIRED_STAGE_MAP[l.stage] },
        { skipFubPush: true },
      ).catch((e) => console.error("Retired-stage migration failed for", l.id, e));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, user?.id]);

  const isPrivileged =
    user?.role === "Admin" || user?.role === "Supervisor" || user?.role === "Accountant";

  // Deleted leads can never arrive through listenListingLeads — it strips
  // deletedAt > 0 before rows reach app state, which is exactly why a deleted
  // lead used to be invisible everywhere. Fetch them separately. The PAGE does
  // this rather than the tab, because the tab's count must exist before anyone
  // opens it.
  const [deletedRows, setDeletedRows] = useState([]);
  const loadDeleted = useCallback(async () => {
    try {
      setDeletedRows((await getDeletedListingLeads()) || []);
    } catch (err) {
      console.error("Could not load deleted listing leads:", err);
    }
  }, []);
  useEffect(() => {
    loadDeleted();
  }, [loadDeleted]);

  const visibleDeleted = useMemo(
    () => selectVisibleDeletedLeads({ rows: deletedRows, user, viewAgent, isPrivileged }),
    [deletedRows, user, viewAgent, isPrivileged],
  );

  const restoreDeleted = async (lead) => {
    try {
      await restoreListingLead(lead.id);
      toast.success("Lead restored");
      loadDeleted();
    } catch (err) {
      console.error("Restore failed:", err);
      toast.error("Could not restore lead");
    }
  };

  const purgeDeleted = async (lead) => {
    const ok = await confirm({
      title: "Delete permanently",
      body: `Permanently delete "${lead.propertyAddress || lead.contactName || "this lead"}"?`,
      note: "This cannot be undone.",
      tone: "danger",
      confirmLabel: "Delete forever",
    });
    if (!ok) return;
    try {
      await permanentlyDeleteListingLead(lead.id);
      toast.success("Deleted forever");
      loadDeleted();
    } catch (err) {
      console.error("Permanent delete failed:", err);
      toast.error("Could not delete lead");
    }
  };

  const canManage = (lead) =>
    user?.role === "Admin" ||
    user?.role === "Supervisor" ||
    isOnLead(lead, user?.id, { ownerlessFallback: true });

  const visibleLeads = useMemo(
    () =>
      leads.filter((l) => {
        // Signed listings leave the active pipeline; "Show Won" brings them
        // back as a fully-converted tracker (dimmed, WON ✓ badge).
        if (l.status === "converted" && !showWon) return false;
        if (!showLost && l.status === "lost") return false;
        if (isPrivileged) {
          if (viewAgent === "all") return true;
          return isOnLead(l, viewAgent);
        }
        // Agents' own board: ownerless leads they entered stay visible to
        // them (they have no "All Agents" view to find them in).
        return isOnLead(l, user?.id, { ownerlessFallback: true });
      }),
    [leads, showLost, showWon, isPrivileged, user?.id, viewAgent],
  );

  // No won-count badge on this toolbar. It counted EVERY signed listing on the
  // team with no agent filter, so an agent read the team's tally as if it were
  // their own (Henry, 2026-08-12: "it doesnt make sense for everyone to see
  // that when it is not their wins"). The "Show Won" checkbox still reveals the
  // won cards, and those ARE scoped by the agent filter below.

  // Board search — filters cards by client name, property address, and agents.
  const searchTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchesQuery = (l) => {
    if (searchTokens.length === 0) return true;
    const haystack = [
      l.contactName,
      l.propertyAddress,
      l.assignedAgentName,
      ...(Array.isArray(l.coAgents) ? l.coAgents.map((c) => c.name) : []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchTokens.every((t) => haystack.includes(t));
  };

  // The blend view gets the already-visible, already-searched leads.
  const shownLeads = useMemo(
    () => visibleLeads.filter(matchesQuery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleLeads, query],
  );

  // Stage moves happen via the row dropdown (and the drawer) — same rules and
  // graduation hint the old drag-and-drop had.
  const moveStage = async (lead, newStage) => {
    if (!lead || !newStage || newStage === lead.stage) return;
    if (!canManage(lead)) {
      toast.error("You can only move your own leads");
      return;
    }
    dispatch({
      type: actionType.STORE_LISTING_LEADS,
      payload: leads.map((l) => (l.id === lead.id ? { ...l, stage: newStage } : l)),
    });
    const res = await updateListingLeadStage(lead.id, newStage);
    if (!res.success) toast.error("Failed to move lead");
    else if (newStage === GRADUATION_STAGE)
      toast.info('At Listing Appointment — once signed, open the card and tap "Sign the Listing" to move it to the Deals Board.');
    else offerListingRequest(lead, newStage);
  };

  // Middle column of a row: the property + money facts an agent scans for.
  const fmtMoney = (v) => {
    const n = Number(String(v ?? "").toString().replace(/[^0-9.]/g, ""));
    return n > 0 ? `$${n.toLocaleString("en-US")}` : "";
  };
  const secondaryLine = (l) =>
    [
      l.propertyAddress,
      fmtMoney(l.listPrice) ? `list ${fmtMoney(l.listPrice)}` : fmtMoney(l.estValue) ? `est ${fmtMoney(l.estValue)}` : "",
    ]
      .filter(Boolean)
      .join(" · ");

  // Extra muted board-card line: where the lead came from. (The price already
  // rides the secondary line next to the address — don't repeat it.)
  const detailLines = (l) => {
    // Legacy/migrated leads occasionally stored the deal's source OBJECT.
    const source =
      l.source && typeof l.source === "object"
        ? flatName(l.source)
        : leadSourceLabel(l.source, leadsource);
    return [source];
  };

  const selectedLead = leads.find((l) => l.id === selectedId) || null;

  // Surface for what the sweep can no longer do silently (Admin-only):
  //  - stuck deals: agent-less prospect files the sweep skips — the board
  //    renders no column for their stages, so without this list they would
  //    exist nowhere a user looks
  //  - suspect leads: leads the OLD sweep mis-assigned to an admin (live
  //    2026-07-29) — open the drawer and hand them to the right agent; a
  //    human reassign drops them off this list automatically
  const [stuckAssigned, setStuckAssigned] = useState(() => new Set());
  const stuckDeals = useMemo(
    () =>
      user?.role === "Admin"
        ? sweepStuckDeals(deals, Object.keys(STAGE_FROM_BOARD)).filter(
            (d) => !stuckAssigned.has(String(d.createdat)),
          )
        : [],
    [user?.role, deals, stuckAssigned],
  );
  // Confirming a suspect needs its SOURCE deal, which is soft-deleted and so
  // never present in the loaded deals list — fetch each candidate's source by
  // id and flag the lead only when that deal can't vouch for any owner.
  const [suspectLeads, setSuspectLeads] = useState([]);
  useEffect(() => {
    if (user?.role !== "Admin") return;
    const adminIds = (users || [])
      .filter((u) => u.role === "Admin")
      .map((u) => u.id);
    const candidates = suspectCandidateLeads(leads, adminIds);
    if (!candidates.length) {
      setSuspectLeads([]);
      return;
    }
    let alive = true;
    (async () => {
      const flagged = [];
      for (const l of candidates) {
        try {
          const src = await getDealById(String(l.sourceDealId));
          if (src && migrationLeadOwner(src) === "") flagged.push(l);
        } catch (e) {
          console.error("Suspect-lead source check failed for", l.id, e);
        }
      }
      if (alive) setSuspectLeads(flagged);
    })();
    return () => {
      alive = false;
    };
  }, [user?.role, users, leads]);

  const assignStuckDeal = async (d, agentId) => {
    const a = (employees || []).find((e) => e.id === agentId);
    if (!a) return;
    try {
      await updateDealById(String(d.createdat), {
        agentData: a.id,
        agentName: [a.firstname, a.lastname].filter(Boolean).join(" "),
      });
      setStuckAssigned((prev) => new Set(prev).add(String(d.createdat)));
      toast.success("Agent set — it moves to the pipeline on the next open");
    } catch (e) {
      console.error("Assigning the stuck file failed:", e);
      toast.error("Couldn't set the agent. Try again.");
    }
  };

  // "Appt Set" offers to start the Listing Request with the lead's data
  // pre-filled (clients + partner, agent, source, appointment date, address,
  // notes). Ask-first — dragging a card must never yank the user into a
  // full-page form. Skipped when a file already exists (sourceDealId).
  const navigate = useNavigate();
  const [apptPromptLead, setApptPromptLead] = useState(null);
  const offerListingRequest = (lead, newStage) => {
    if (newStage !== "Appt Set") return;
    if (lead?.sourceDealId) return;
    setApptPromptLead(lead);
  };
  const openPrefilledRequest = () => {
    const prefill = buildListingRequestPrefill(apptPromptLead, leadsource);
    setApptPromptLead(null);
    setSelectedId(null);
    navigate("/dashboard/listing-request?type=seller", {
      state: { listingRequestPrefill: prefill },
    });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <PageHeader
        title="Listing Pipeline"
        actions={
          <>
            {isPrivileged && (
              <select value={viewAgent} onChange={(e) => setViewAgent(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700">
                <option value="all">👁 Viewing: All Agents</option>
                {excludeTestAgents(employees).map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {[emp.firstname, emp.lastname].filter(Boolean).join(" ") || emp.email}
                  </option>
                ))}
              </select>
            )}
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showLost} onChange={(e) => setShowLost(e.target.checked)} />
              Show Lost
            </label>
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showWon} onChange={(e) => setShowWon(e.target.checked)} />
              Show Won
            </label>
            <button onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 bg-[#1E293B] hover:opacity-90 text-white rounded-full px-4 py-2 text-sm font-semibold">
              <Plus size={16} /> Add Listing Lead
            </button>
          </>
        }
      />

      {/* Search — its own row beneath the header. */}
      <div className="px-3 pt-3 pb-1">
        <div className="relative w-full max-w-md">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search listings — name, address…"
            className="w-full text-sm border border-slate-200 rounded-lg pl-8 pr-7 py-1.5 bg-white text-slate-700 placeholder:text-slate-400"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {(stuckDeals.length > 0 || suspectLeads.length > 0) && (
        <div className="mx-3 mt-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {stuckDeals.length > 0 && (
            <div>
              <div className="font-semibold mb-1">
                {stuckDeals.length === 1
                  ? "1 file needs an agent before it can move to the pipeline"
                  : `${stuckDeals.length} files need an agent before they can move to the pipeline`}
              </div>
              <ul className="space-y-1">
                {stuckDeals.map((d) => (
                  <li
                    key={d.createdat}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <span>
                      {d.propertyaddress ||
                        d.clientLastName ||
                        `File ${d.createdat}`}
                    </span>
                    <select
                      defaultValue=""
                      onChange={(e) =>
                        e.target.value && assignStuckDeal(d, e.target.value)
                      }
                      className="text-xs border border-amber-300 rounded-md px-1.5 py-1 bg-white text-slate-700"
                    >
                      <option value="">Assign agent…</option>
                      {excludeTestAgents(employees || []).map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {[emp.firstname, emp.lastname]
                            .filter(Boolean)
                            .join(" ") || emp.email}
                        </option>
                      ))}
                    </select>
                    <a
                      href={`#/dashboard/deals-detail/${d.createdat}`}
                      className="text-xs text-amber-700 underline"
                    >
                      view
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {suspectLeads.length > 0 && (
            <div
              className={
                stuckDeals.length > 0 ? "mt-2 pt-2 border-t border-amber-200" : ""
              }
            >
              <div className="font-semibold mb-1">
                {suspectLeads.length} lead{suspectLeads.length > 1 ? "s" : ""}{" "}
                may be assigned to the wrong person (from an earlier board move)
              </div>
              <ul className="space-y-1">
                {suspectLeads.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(l.id)}
                      className="underline text-amber-800 hover:text-amber-900"
                    >
                      {l.contactName || l.propertyAddress || "Unnamed lead"}
                    </button>
                    <span className="text-xs text-amber-700">
                      {" "}
                      — open it and set the right agent
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        <PipelineViewSwitch
          leads={shownLeads}
          stages={LISTING_STAGES}
          parkingStages={PARKING_STAGES}
          stageDot={(stage) => (
            <span
              className="rounded-full size-2.5 inline-block flex-shrink-0"
              style={{ backgroundColor: STAGE_COLORS[stage] }}
            />
          )}
          stageColor={getListingStageColor}
          graduationStage={GRADUATION_STAGE}
          wonLabel="Won · Signed Listing"
          apptLabel="Listing appt"
          renderSecondary={secondaryLine}
          renderDetails={detailLines}
          canManage={canManage}
          deletedRows={visibleDeleted}
          deletedCount={visibleDeleted.length}
          onRestoreDeleted={restoreDeleted}
          onPurgeDeleted={purgeDeleted}
          onOpen={(lead) => setSelectedId(lead.id)}
          onStageChange={moveStage}
          onDismiss={async (lead) => {
            // Snooze = schedule a touch a week out.
            const res = await updateListingLead(lead.id, { nextFollowUp: isoDaysFromNow(7) });
            if (res.success) toast.success("Snoozed a week");
            else toast.error("Couldn't dismiss. Try again.");
          }}
          onMarkLost={async (lead) => {
            // Same flow as the drawer's ⋮ menu.
            const reason = await prompt({
              title: "Mark as Lost",
              label: "Reason this lead is lost or inactive (optional)",
              placeholder: "Went with another agent",
              confirmLabel: "Mark as Lost",
            });
            if (reason === null) return; // cancelled — leave the lead alone
            const res = await updateListingLead(lead.id, {
              status: "lost",
              lostReason: reason,
              lostAt: Date.now(),
            });
            if (res.success) {
              // The Goals log classifies its appointment itself.
              classifyAppointmentForLead(lead, "lost");
              toast.success("Marked as Lost");
            } else toast.error("Failed");
          }}
          onDelete={async (lead) => {
            const name = clientNames(lead) || "this lead";
            const ok = await confirm({
              title: "Delete lead",
              body: `Delete ${name} from the pipeline?`,
              note: "This removes them from the board entirely.",
              tone: "danger",
              confirmLabel: "Delete",
            });
            if (!ok) return;
            const res = await deleteListingLead(lead.id);
            if (res.success) toast.success("Lead deleted");
            else toast.error("Couldn't delete. Try again.");
          }}
        />
      </div>

      {addOpen && (
        <NewListingLeadModal
          onClose={() => setAddOpen(false)}
          onCreated={(lead) => lead && offerListingRequest(lead, lead.stage)}
        />
      )}
      {selectedLead && (
        <ListingLeadDrawer
          lead={selectedLead}
          canManage={canManage(selectedLead)}
          onClose={() => setSelectedId(null)}
          onStageSet={(lead, newStage) => offerListingRequest(lead, newStage)}
        />
      )}
      {apptPromptLead && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => setApptPromptLead(null)}
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 pointer-events-auto">
              <div className="text-base font-semibold text-slate-900 mb-1">
                Appointment set
                {apptPromptLead.contactName
                  ? ` for ${apptPromptLead.contactName}`
                  : ""}
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Want to start the Listing Request now? Everything you've
                entered — clients, agent, source, appointment date, address,
                notes — comes along pre-filled.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setApptPromptLead(null)}
                  className="text-sm text-slate-600 hover:text-slate-800 px-3 py-2"
                >
                  Not yet
                </button>
                <button
                  onClick={openPrefilledRequest}
                  className="text-sm font-semibold bg-[#1E293B] text-white rounded-lg px-4 py-2"
                >
                  Open Listing Request
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {dialog}
    </div>
  );
}
